import React from 'react'
import cn from 'classnames'
import styles from './user-name.module.scss'
import moment from 'moment'
import WithTooltip from 'components/shared/WithTooltip'
import useStore from 'state/knovStore'
import { getAgentModelDisplayLabel } from 'lib/agentModels'
import { isMobile } from 'lib/device'
import { resolveUser } from 'state/cache'
import { formatRelativeTimeLabel } from 'lib/relativeTimeLabel'

export default function UserName(props) {
    const spaceName = useStore(state => state.getActiveSpace)()?.name
    const { user, virtualUserName, transcriptSegmentInfo } = props
    const resolvedUser = resolveUser(user, user?.id)

    let flexDirection, margin
    if (props.column) {
        flexDirection = styles.flexColumn
    } else {
        flexDirection = styles.flexRow
        margin = styles.horizMargin
    }

    // An explicit per-answer display name (agents, market cards, the radio
    // station) outranks the space-name stand-in used for system posts.
    const name = virtualUserName || (props.showKnovigator ? spaceName : resolvedUser?.name)

    return (
        <div className={cn(styles.userNameComp, props.contextStyles)}>
            {!props.hideName && (
                <div className={cn(styles.usernameWrapper)}>
                    <span className={cn(styles.username)}>
                        {name}
                        {props.agentModel && (
                            <span className={cn(styles.agentModel)}>
                                {getAgentModelDisplayLabel(props.agentModel) || props.agentModel}
                            </span>
                        )}
                    </span>
                </div>
            )}

            <div className={cn(styles.timeContainer, props.isTitle && styles.titleTimeContainer)}>
                {props.showAgg && (
                    <Time
                        time={props.lastAnswerCreatedAt}
                        tip={'Time of latest message in thread.'}
                        thread
                    />
                )}

                {props.showAgg && <>|{/*•*/}</>}

                {props.answerCreatedAt && (
                    <Time
                        answer={props?.answer}
                        time={props.answerCreatedAt}
                        transcriptSegmentInfo={transcriptSegmentInfo}
                        tip={new Date(props.answerCreatedAt).toLocaleString()}
                        permalinkHref={props.permalinkHref}
                    />
                )}
            </div>
        </div>
    )
}

function Time(props) {
    const { thread, tip, time, transcriptSegmentInfo } = props
    const onPermalinkClick = ev => {
        if (ev.button !== 0 || ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) {
            // Keep the enclosing card handler from cancelling native new-tab
            // and new-window gestures without cancelling the anchor itself.
            ev.stopPropagation()
        }
    }
    const formatTranscriptSegmentInfo = segmentInfo => {
        const formatAsHHMMSS = seconds => {
            const date = new Date(null)
            const duration = moment.duration(seconds, 'seconds')
            const formattedDuration =
                duration.asHours() < 1
                    ? moment.utc(duration.asMilliseconds()).format('mm:ss')
                    : moment.utc(duration.asMilliseconds()).format('HH:mm:ss')

            return formattedDuration
        }
        return `${formatAsHHMMSS(segmentInfo.start)} - ${formatAsHHMMSS(segmentInfo.end)}`
    }

    const threadStyles = thread ? styles.thread : null
    const timeLabel = transcriptSegmentInfo
        ? formatTranscriptSegmentInfo(transcriptSegmentInfo)
        : formatRelativeTimeLabel(time)

    return (
        <div className={styles.timeComp}>
            <WithTooltip tip={tip}>
                {props.permalinkHref ? (
                    // Timestamp-as-permalink: the real <a href> crawlers follow
                    // into the thread (rendered cards otherwise carry no thread
                    // links at all). Plain clicks bubble to the card's existing
                    // navigation; modified clicks keep native link behaviour.
                    <a
                        className={cn(styles.timePermalink, threadStyles)}
                        href={props.permalinkHref}
                        onClick={onPermalinkClick}
                    >
                        {timeLabel}
                    </a>
                ) : (
                    <div className={cn(threadStyles)}>{timeLabel}</div>
                )}
            </WithTooltip>
        </div>
    )
}

export { Time }
